import React, { useState, useEffect } from 'react';
import { FileText, Search, Grid, List, Clock, AlertCircle, ArrowRight, Loader, Tag, BarChart2, Table, RefreshCw, FileIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { formatBytes, formatDate } from '../utils/formatters';
import { config } from '../utils/config';
import { apiClient } from '../api/client';
import PDFViewer from './PDFViewer';

interface Document {
  id: number;
  filename: string;
  file_size: number;
  total_pages: number;
  upload_date: string;
  last_analysis: {
    status: string;
    entity_count: number;
  };
}

interface Entity {
  id: number;
  text: string;
  label: string;
  confidence: number;
  context: string;
  start_char: number;
  end_char: number;
}

interface AnalysisResponse {
  entities: Record<string, Entity[]>;
  total_count: number;
}

const DocumentsPage = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [analysisDetails, setAnalysisDetails] = useState<AnalysisResponse | null>(null);
  const [flattenedEntities, setFlattenedEntities] = useState<Entity[]>([]);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'document' | 'entities' | 'tables'>('overview');

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    if (selectedDocument) {
      fetchAnalysisDetails(selectedDocument.id);
    } else {
      setAnalysisDetails(null);
      setFlattenedEntities([]);
      setAnalysisError(null);
    }
  }, [selectedDocument]);

  // Transform the entities when analysisDetails changes
  useEffect(() => {
    if (analysisDetails && analysisDetails.entities) {
      // Flatten the nested entities structure into a simple array
      const allEntities: Entity[] = [];
      Object.keys(analysisDetails.entities).forEach(label => {
        const entitiesForLabel = analysisDetails.entities[label] || [];
        allEntities.push(...entitiesForLabel);
      });
      setFlattenedEntities(allEntities);
    } else {
      setFlattenedEntities([]);
    }
  }, [analysisDetails]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(config.endpoints.documents);
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysisDetails = async (documentId: number) => {
    try {
      setLoadingAnalysis(true);
      setAnalysisError(null);
      setAnalysisDetails(null);
      
      const response = await apiClient.get(`${config.endpoints.documents}/${documentId}/entities`);
      setAnalysisDetails(response.data);
    } catch (err) {
      console.error('Error fetching analysis:', err);
      if (err instanceof Error) {
        setAnalysisError(err.message);
      } else {
        setAnalysisError('Failed to load entity data');
      }
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleRefreshAnalysis = () => {
    if (selectedDocument) {
      fetchAnalysisDetails(selectedDocument.id);
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderDocumentCard = (document: Document) => (
    <Card 
      key={document.id}
      className="hover:bg-purple-500/10 transition-all duration-200 cursor-pointer"
      onClick={() => setSelectedDocument(document)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <FileText className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-base text-purple-100 truncate">
                {document.filename}
              </CardTitle>
              <p className="text-sm text-purple-400/60">
                {formatBytes(document.file_size)} • {document.total_pages} pages
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-purple-400/60">
            <Clock className="w-4 h-4" />
            {formatDate(document.upload_date)}
          </div>
          <div className={`px-2 py-1 rounded-full text-xs 
            ${document.last_analysis?.status === 'completed' 
              ? 'bg-green-500/20 text-green-300' 
              : document.last_analysis?.status === 'failed'
              ? 'bg-red-500/20 text-red-300'
              : 'bg-purple-500/20 text-purple-300'
            }`}>
            {document.last_analysis?.status || 'Processing'}
          </div>
        </div>
      </CardContent>
    </Card>
  );
  
  const renderDocumentRow = (document: Document) => (
    <div
      key={document.id}
      className="flex items-center gap-4 p-4 hover:bg-purple-500/10 transition-all duration-200 cursor-pointer rounded-lg"
      onClick={() => setSelectedDocument(document)}
    >
      <div className="p-2 rounded-lg bg-purple-500/20">
        <FileText className="w-6 h-6 text-purple-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base text-purple-100 truncate">{document.filename}</h3>
        <p className="text-sm text-purple-400/60">
          {formatBytes(document.file_size)} • {document.total_pages} pages
        </p>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-sm text-purple-400/60">
          {formatDate(document.upload_date)}
        </div>
        <div className={`px-2 py-1 rounded-full text-xs 
          ${document.last_analysis?.status === 'completed' 
            ? 'bg-green-500/20 text-green-300' 
            : document.last_analysis?.status === 'failed'
            ? 'bg-red-500/20 text-red-300'
            : 'bg-purple-500/20 text-purple-300'
          }`}>
          {document.last_analysis?.status || 'Processing'}
        </div>
      </div>
    </div>
  );

  const renderEntityCard = (entity: Entity) => (
    <Card key={entity.id} className="bg-purple-500/5">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-purple-300">{entity.label}</span>
              <span className="text-xs text-purple-400/60">
                {(entity.confidence * 100).toFixed(1)}% confidence
              </span>
            </div>
            <p className="text-base text-white">{entity.text}</p>
            <p className="mt-2 text-sm text-purple-400/60 line-clamp-2">{entity.context}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Group entities by their label
  const groupedEntities = analysisDetails?.entities || {};

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Documents</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg ${
              viewMode === 'grid' ? 'bg-purple-500/20' : 'hover:bg-purple-500/10'
            }`}
          >
            <Grid className="w-5 h-5 text-purple-300" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg ${
              viewMode === 'list' ? 'bg-purple-500/20' : 'hover:bg-purple-500/10'
            }`}
          >
            <List className="w-5 h-5 text-purple-300" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400/60 w-5 h-5" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-100 placeholder-purple-400/60 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <Loader className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-200">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredDocuments.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-purple-400/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-purple-100">No Documents Found</h3>
          <p className="text-purple-400/60 mt-1">
            {searchTerm ? 'Try adjusting your search terms' : 'Upload your first document to get started'}
          </p>
        </div>
      )}

      {/* Document Grid/List */}
      {!loading && !error && filteredDocuments.length > 0 && (
        <div className={
          viewMode === 'grid'
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            : "space-y-2"
        }>
          {filteredDocuments.map(doc =>
            viewMode === 'grid' ? renderDocumentCard(doc) : renderDocumentRow(doc)
          )}
        </div>
      )}

      {/* Document Details Slide-Over */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
          <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-gray-900 shadow-xl">
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-purple-500/20">
                <h2 className="text-lg font-medium text-purple-100">
                  Document Details
                </h2>
                <button
                  onClick={() => {
                    setSelectedDocument(null);
                    setAnalysisDetails(null);
                    setActiveTab('overview');
                    setAnalysisError(null);
                  }}
                  className="text-purple-400 hover:text-purple-300"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-purple-500/20">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'overview' 
                      ? 'text-purple-300 border-b-2 border-purple-500' 
                      : 'text-purple-400/60 hover:text-purple-300'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('document')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'document' 
                      ? 'text-purple-300 border-b-2 border-purple-500' 
                      : 'text-purple-400/60 hover:text-purple-300'
                  }`}
                >
                  Document
                </button>
                <button
                  onClick={() => setActiveTab('entities')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'entities' 
                      ? 'text-purple-300 border-b-2 border-purple-500' 
                      : 'text-purple-400/60 hover:text-purple-300'
                  }`}
                >
                  Entities
                </button>
                <button
                  onClick={() => setActiveTab('tables')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'tables' 
                      ? 'text-purple-300 border-b-2 border-purple-500' 
                      : 'text-purple-400/60 hover:text-purple-300'
                  }`}
                >
                  Tables
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {loadingAnalysis && activeTab !== 'document' ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center">
                      <Loader className="w-8 h-8 text-purple-400 animate-spin mb-4" />
                      <p className="text-purple-300">Loading analysis details...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {activeTab === 'overview' && (
                      <div className="p-6 space-y-6">
                        <div>
                          <h3 className="text-sm font-medium text-purple-400">Filename</h3>
                          <p className="mt-1 text-purple-100">{selectedDocument.filename}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h3 className="text-sm font-medium text-purple-400">Size</h3>
                            <p className="mt-1 text-purple-100">
                              {formatBytes(selectedDocument.file_size)}
                            </p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-purple-400">Pages</h3>
                            <p className="mt-1 text-purple-100">
                              {selectedDocument.total_pages}
                            </p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-purple-400">Upload Date</h3>
                            <p className="mt-1 text-purple-100">
                              {formatDate(selectedDocument.upload_date)}
                            </p>
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-purple-400">Analysis Status</h3>
                            <p className="mt-1">
                              <span className={`px-2 py-1 rounded-full text-xs 
                                ${selectedDocument.last_analysis?.status === 'completed' 
                                  ? 'bg-green-500/20 text-green-300' 
                                  : selectedDocument.last_analysis?.status === 'failed'
                                  ? 'bg-red-500/20 text-red-300'
                                  : 'bg-purple-500/20 text-purple-300'
                                }`}>
                                {selectedDocument.last_analysis?.status || 'Processing'}
                              </span>
                            </p>
                          </div>
                        </div>
                        {analysisDetails && (
                          <div className="pt-6 border-t border-purple-500/20">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-sm font-medium text-purple-400">Analysis Summary</h3>
                              <button 
                                onClick={handleRefreshAnalysis}
                                className="flex items-center gap-1 text-purple-400 hover:text-purple-300 text-xs"
                              >
                                <RefreshCw className="w-3 h-3" />
                                Refresh
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <Card className="bg-purple-500/5">
                                <CardContent className="p-4">
                                  <div className="flex items-center gap-2">
                                    <BarChart2 className="w-5 h-5 text-purple-400" />
                                    <div>
                                      <p className="text-sm text-purple-400">Total Entities</p>
                                      <p className="text-2xl font-bold text-purple-100">
                                        {analysisDetails.total_count}
                                      </p>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                              <Card className="bg-purple-500/5">
                                <CardContent className="p-4">
                                  <div className="flex items-center gap-2">
                                    <Tag className="w-5 h-5 text-purple-400" />
                                    <div>
                                      <p className="text-sm text-purple-400">Entity Types</p>
                                      <p className="text-2xl font-bold text-purple-100">
                                        {Object.keys(groupedEntities).length}
                                      </p>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'document' && (
                      <div className="h-full">
                        <PDFViewer 
                          documentId={selectedDocument.id} 
                          filename={selectedDocument.filename}
                        />
                      </div>
                    )}

                    {activeTab === 'entities' && (
                      <div className="p-6">
                        {analysisError ? (
                          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
                            <div className="flex items-center gap-2 text-red-200">
                              <AlertCircle className="w-5 h-5" />
                              <p>{analysisError}</p>
                            </div>
                            <div className="mt-4 flex justify-center">
                              <button
                                onClick={handleRefreshAnalysis}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300"
                              >
                                <RefreshCw className="w-4 h-4" />
                                Try Again
                              </button>
                            </div>
                          </div>
                        ) : analysisDetails && Object.keys(groupedEntities).length > 0 ? (
                          <div className="space-y-8">
                            {Object.entries(groupedEntities).map(([label, entities]) => (
                              <div key={label}>
                                <h3 className="text-lg font-medium text-purple-100 mb-4 flex items-center gap-2">
                                  <Tag className="w-4 h-4 text-purple-400" />
                                  {label}
                                  <span className="text-sm text-purple-400/60">({entities.length})</span>
                                </h3>
                                <div className="space-y-4">
                                  {entities.map(entity => renderEntityCard(entity))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <Tag className="w-12 h-12 mx-auto mb-4 opacity-40" />
                            <p className="text-purple-400/60">No entities found in this document</p>
                            <button
                              onClick={handleRefreshAnalysis}
                              className="mt-4 flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300 mx-auto"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Refresh Analysis
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'tables' && (
                      <div className="text-center py-12">
                        <Table className="w-12 h-12 mx-auto mb-4 opacity-40" />
                        <p className="text-purple-400/60">Table extraction coming soon</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;